import { userApi } from 'api';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { Specialization, User, UserPosition } from 'types/api';
import { Interview } from 'types/interview';
import { authUtils } from 'utils/auth';
import { interviewStore } from './interviewStore';
import { RequestedInterview } from 'types/vacancy';
import { orderBy } from 'lodash';

export class PersonalDataStore {
  user: User | null = null;

  userId: string | null = null;

  accessToken: string | null = null;

  userLoading = false;

  userError: string | null = null;

  isAttemptingRestore = false;

  specializations: Specialization[] = [];

  positions: UserPosition[] = [];

  private skillsStore: {
    getSkillsWithGrade: () => Promise<void>;
  } | null = null;

  private storageListenerBound = false;

  private skipNextUserFetch = false;

  payments = [] as any[];

  discount = null as any;

  extractInterviews: any[] = [];

  notUsedPayments = [] as any[];

  requestedInterviews = [] as RequestedInterview[];

  data: User = {
    id: undefined,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    speciality: '',
    specialityLevel: 'MIDDLE',
    position: 'frontend-developer',
    isActive: true,
    activeInterviews: []
  };

  interviewList: Interview[] = [];

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    this.initializeAuthState();
    interviewStore.attachPersonalDataStore(this);
    reaction(
      () => this.userId,
      (nextUserId) => {
        if (!nextUserId) {
          runInAction(() => {
            this.user = null;
            this.data.id = undefined;
          });
          return;
        }
        if (this.skipNextUserFetch) {
          this.skipNextUserFetch = false;
          return;
        }
        void this.fetchAndApplyUser(nextUserId);
      },
      { fireImmediately: true }
    );
  }

  attachSkillsStore(store: { getSkillsWithGrade: () => Promise<void> }) {
    this.skillsStore = store;
  }

  get allTestedSkills() {
    // Сортируем завершенные интервью по дате (самые свежие первыми)
    const sortedInterviews = this.interviewList
      .filter((i) => i.finish && i.report)
      .slice()
      .sort(
        (a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
      );

    // Для каждого уникального title берем оценку из самого свежего интервью
    const latestScores = sortedInterviews.reduce((acc, interview) => {
      if (!interview.result?.sections) {
        return acc;
      }

      interview.result.sections.forEach((section) => {
        const title = section.title;
        // Берем оценку только если она еще не была установлена (т.е. из более свежего интервью)
        if (!acc[title]) {
          acc[title] = Number(section.score) || 0;
        }
      });

      return acc;
    }, {} as Record<string, number>);

    return latestScores;
  }

  private initializeAuthState() {
    this.userId = authUtils.getUserId();
    this.accessToken = authUtils.getAccessToken();
    if (typeof window !== 'undefined' && !this.storageListenerBound) {
      window.addEventListener('storage', this.handleStorageChange);
      this.storageListenerBound = true;
    }
  }

  private handleStorageChange = () => {
    const nextAccessToken = authUtils.getAccessToken();
    const nextUserId = authUtils.getUserId();
    const shouldReloadUser = nextUserId !== this.userId;
    runInAction(() => {
      this.accessToken = nextAccessToken;
      this.userId = nextUserId;
    });
    if (shouldReloadUser && nextUserId) {
      void this.fetchAndApplyUser(nextUserId);
    }
  };

  async fetchAndApplyUser(id: string) {
    this.userLoading = true;
    this.userError = null;
    try {
      const fetchedUser = await userApi.getUserById(id);
      runInAction(() => {
        this.user = fetchedUser;
        this.setFromUser(fetchedUser);
      });
      const promises = [
        this.skillsStore?.getSkillsWithGrade(),
        this.getPayments(),
        this.getInterviewList(),
        this.getRequestedInterviews(),
        this.getExtractInterviews()
      ];
      await Promise.all(promises);
    } catch (error: any) {
      runInAction(() => {
        this.userError = 'Не удалось загрузить пользователя';
        // Сбрасываем токены только при 401/403. При network/SSL ошибках — сохраняем токены
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('userId');
          localStorage.removeItem('refreshToken');
        }
      });
    } finally {
      runInAction(() => {
        this.userLoading = false;
      });
    }
  }

  setUser(user: User | null) {
    this.user = user;
    if (user) {
      this.setFromUser(user);
    }
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  setUserId(nextUserId: string | null) {
    this.userId = nextUserId;
  }

  setField = (field: keyof User, value: string) => {
    this.data[field as keyof User] = value as never;
  };

  setFromUser = (user: User) => {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';

    this.data = {
      id: user.id ? String(user.id) : undefined,
      firstName,
      lastName,
      email: user.email || '',
      phone: user.phone || '',
      speciality: user.speciality || '',
      specialityLevel: user.specialityLevel,
      position: user.position || this.data.position,
      activeInterviews: user.activeInterviews
    };
  };

  //Получить список интервью пользователя
  getInterviewList = async () => {
    if (!this.data.id) return;
    const response = await userApi.getInterviewList(this.data.id);
    this.interviewList = response;
  };

  updateUser = async (payload: Partial<User>) => {
    if (!this.data.id) {
      return;
    }
    const response = await userApi.updateUser(this.data.id, payload);
    this.setFromUser({ ...response, id: this.data.id } as unknown as User);
  };

  getPayments = async () => {
    try {
      const payments = await userApi.getPayments();
      const notUsedPayments = payments.filter((payment) => !payment.isUsed);
      runInAction(() => {
        this.payments = payments;
        this.notUsedPayments = notUsedPayments;
      });
    } catch (e) {
      console.error(e);
    }
  };

  getDiscounts = async () => {
    try {
      if(!personalDataStore.user?.id) return

      const discount = await userApi.getDiscounts(personalDataStore.user?.id);
      runInAction(() => {
        this.discount = discount
      });
    } catch (e) {
      console.error(e);
    }
  };

  getRequestedInterviews = async () => {
    if (!this.data.id || !this.data.email) return;
    const response = await userApi.requestedInterviews(this.data.id);
    runInAction(() => {
      this.requestedInterviews = orderBy(response.filter(i => i.status !== "COMPLETED"), 'createdAt', 'desc');
    });
  };

  async getExtractInterviews(): Promise<void> {
    try {
      const interviews = await userApi.getExtractInterviews(this.user?.id as string);
      runInAction(() => {
        this.extractInterviews = interviews;
      });
    } catch (error) {
      console.error(error);
    }
  }

  addinterviewtoUser = async (interview: Interview) => {
    if (!this.data.id) {
      return;
    }

    const response = await userApi.addInterviewToUser(this.data.id, {
      ...interview,
      position: this.data.position
    });
    this.data.activeInterviews = response.activeInterviews
  };

  updateUserInterview = async (interviewId: string, payload: Partial<Interview>) => {
    if (!this.data.id) {
      return;
    }
    await userApi.updateUserInterview(interviewId, payload);
  };

  reset = () => {
    this.user = null;
    this.userId = null;
    this.accessToken = null;
    this.userError = null;
    this.userLoading = false;
    this.data = {
      id: undefined,
      firstName: '',
      lastName: '',
      position: '',
      email: '',
      phone: '',
      speciality: '',
      specialityLevel: 'MIDDLE',
      isActive: true,
      activeInterviews: []
    };
  };

  login = async (
    accessToken: string,
    refreshToken: string,
    userId: string | undefined,
    callback: (user: User) => void
  ) => {
    if (!userId) {
      return;
    }
    authUtils.setTokens(accessToken, refreshToken);
    authUtils.setUserId(userId);
    runInAction(() => {
      this.accessToken = accessToken;
      this.userId = userId;
      this.skipNextUserFetch = true;
    });
    try {
      const user = await userApi.getUserById(userId);
      runInAction(() => {
        this.user = user;
        this.setFromUser(user);
      });
      await Promise.all([
        this.skillsStore?.getSkillsWithGrade(),
        this.getInterviewList(),
        this.getPayments(),
        this.getRequestedInterviews(),
        this.getExtractInterviews()
      ]);
      callback(user);
      window.ym(108450737,'reachGoal','registration')
    } catch (error) {
      runInAction(() => {
        this.userError = 'Не удалось загрузить пользователя';
      });
      throw error;
    }
  };

  logout = () => {
    authUtils.clearAuth();
    this.reset();
  };

  refreshUser = async () => {
    if (!this.userId) {
      return;
    }
    this.userLoading = true;
    this.userError = null;
    try {
      const refreshedUser = await userApi.getUserById(this.userId);
      runInAction(() => {
        this.user = refreshedUser;
        this.setFromUser(refreshedUser);
      });
    } catch (error) {
      runInAction(() => {
        this.userError = 'Не удалось загрузить пользователя';
      });
    } finally {
      runInAction(() => {
        this.userLoading = false;
      });
    }
  };

  fetchSpecializations = async (_search: string) => {
    const response = await userApi.getSpecializations('');
    runInAction(() => {
      this.specializations = response;
    });
  };

  fetchPositionsBySpecializationId = async (search: string, id: number) => {
    const response = await userApi.searchPositionsBySpecialization(search, id);
    runInAction(() => {
      this.positions = response;
    });
  };

  fetchAllPositions = async (search: string) => {
    const response = await userApi.searchPositions(search);
    runInAction(() => {
      this.positions = response;
    });
  };

  fetchSpecializationsByPositionName = async (name: string) => {
    const response = await userApi.getSpecializationsByPositionName(name);
    runInAction(() => {
      this.specializations = response;
    });
  };

  attemptRestoreSession = async (): Promise<boolean> => {
    const refreshToken = authUtils.getRefreshToken();
    if (!refreshToken) {
      return false;
    }
    this.isAttemptingRestore = true;
    this.userError = null;
    try {
      const response = await userApi.refreshToken(refreshToken);
      const storedUserId = authUtils.getUserId();
      if (!storedUserId) {
        return false;
      }
      authUtils.updateAccessToken(response.accessToken);
      runInAction(() => {
        this.accessToken = response.accessToken;
      });
      await this.fetchAndApplyUser(storedUserId);
      return true;
    } catch (error: any) {
      // Сбрасываем токены только при 401/403. При network/SSL ошибках — сохраняем для повторной попытки
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        authUtils.clearAuth();
        this.reset();
      }
      return false;
    } finally {
      runInAction(() => {
        this.isAttemptingRestore = false;
      });
    }
  };

  get isAuthenticated() {
    return Boolean(this.accessToken && this.userId);
  }
}

export const personalDataStore = new PersonalDataStore();
